import useUserStore from "@/store/user";
import {PostHog} from "posthog-js";
import {PostHogProvider} from "posthog-js/react";
import type {FC, PropsWithChildren} from "react";

export const capturePostHogEvent = (eventName: PostHogEvents, data: Record<string, unknown>): void => {
  try {
    const getProperties = useUserStore.getState().getPosthogProperties;

    posthog.capture(eventName, {
      ...getProperties(),
      ...data,
    });
  } catch (error) {
    console.error(`Failed to capture PostHog event "${eventName}":`, error);
  }
};

export const posthog = new PostHog();

if (import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true") {
  posthog.init(import.meta.env.VITE_POSTHOG_PUBLIC_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_PUBLIC_HOST,
  });

  posthog.set_config({
    disable_session_recording:
      typeof process.env.ENV_IS_PRODUCTION === "undefined"
        ? import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "false"
        : process.env.ENV_IS_PRODUCTION === "false",
    enable_heatmaps: true,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {password: true},
    },
    capture_heatmaps: true,
    capture_dead_clicks: true,
    capture_performance: true,
    autocapture: false,
    capture_exceptions: false,
  });
}

export enum PostHogEvents {
  RefluencedAppOpened = "refluenced_app_opened",
  StartCampaignCreation = "start_campaign_creation",
  ContinueDraftCampaignCreation = "continue_draft_campaign_creation",
  CampaignCreationCancelled = "campaign_creation_cancelled",
  DraftCampaignDeleted = "draft_campaign_deleted",
  PublishedCampaignDeleted = "published_campaign_deleted",
  DuplicateAsTiktokDraft = "duplicate_as_tiktok_draft",
  DuplicateAsInstagramDraft = "duplicate_as_instagram_draft",
  DuplicateAsUGCDraft = "duplicate_as_ugc_draft",
  SaveAsDraft = "save_as_draft",
  PublishCampaign = "publish_campaign",
  StepTwoCampaignCreation = "step_two_campaign_creation",
  StepThreeCampaignCreation = "step_three_campaign_creation",
  InfluencerApplied = "influencer_applied",
  ApplicationsPageViewed = "applications_page_viewed",
  InfluencerAccepted = "influencer_accepted",
  InfluencerDeclined = "influencer_declined",
  InfluencerCardClicked = "influencer_card_clicked",
  RevertInfluencerDecisionBackToPending = "revert_influencer_decision_back_to_pending",
  StartCampaign = "start_campaign",
  SurveyAfterCampaignCreationAUG2025 = "survey_after_campaign_creation_AUG2025",
  SurveyAfterReportingAUG2025 = "survey_after_reporting_AUG2025",
}

export enum PostHogGroups {
  Agency = "agency",
  Company = "company",
  Subbrand = "subbrand",
}

if (
  typeof process.env.ENV_IS_PRODUCTION === "undefined"
    ? import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "false"
    : process.env.ENV_IS_PRODUCTION === "false"
) {
  posthog.debug(false);
}

const SharedPostHogProvider: FC<PropsWithChildren & {enabled: boolean}> = ({children, enabled}) => {
  if (!enabled) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};

export default SharedPostHogProvider;
